"use client";

import type { PipelineCandidate } from "@/lib/api/types";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { useSyncFlow } from "@/features/ats-sync/components/sync/use-sync-flow";
import { SyncColumnIndicator } from "@/features/ats-sync/components/sync/sync-column-indicator";
import { usePipelineSelection } from "../selection-context";

/**
 * Dedicated per-candidate ATS sync-state column. Only renders when the job is
 * ATS-linked OR its link is dormant (`paused`). Single centered indicator per
 * state (logo badge / ping dot / icon); clicking not-synced or failed opens sync.
 */
export function SyncCell({ candidate }: { candidate: PipelineCandidate }) {
  const { job, stages, onRefresh } = usePipelineJob();
  const { isLinked, dormant, syncDirection } = useJobAtsLink(job.id);
  const { open: openSyncFlow } = useSyncFlow();
  const { onClearSelection } = usePipelineSelection();
  const paused = dormant != null;
  if (!isLinked && !paused) return null;

  const candidateStage = stages.find((s) => s.id === candidate.stage_id);
  const isInSystemSourced =
    !!candidateStage?.is_system && candidateStage?.position === 0;

  const handleSync = () => {
    openSyncFlow({
      jobId: job.id,
      jobCandidateIds: [candidate.job_candidate_id],
      names: candidate.full_name
        ? { [candidate.job_candidate_id]: candidate.full_name }
        : undefined,
      onDone: () => {
        onClearSelection();
        onRefresh?.();
      },
    });
  };

  return (
    <SyncColumnIndicator
      state={candidate.ats_sync_state}
      error={candidate.ats_sync_error}
      pendingAction={candidate.ats_sync_pending_action}
      direction={syncDirection}
      paused={paused}
      isInSystemSourced={isInSystemSourced}
      onSyncClick={handleSync}
    />
  );
}
